import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Collapse,
  CircularProgress,
  Alert,
  Divider,
  useTheme,
} from '@mui/material';
import { Add as AddIcon, Note as NoteIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format, parseISO } from 'date-fns';
import { treatmentSheetApi } from '../../utils/api';
import { useRBAC } from '../../auth/useRBAC';
import { treatmentSheetControls } from '../../auth/pageControls';
import type {
  ClinicalNote,
  ClinicalNoteType,
  AddNotePayload,
} from '../../types/treatmentSheet';

// ─── Constants ────────────────────────────────────────────────────────────────

const NOTE_TYPE_LABELS: Record<ClinicalNoteType, string> = {
  VET_ROUND: 'Vet Round',
  SHIFT_HANDOFF: 'Shift Handoff',
  PROGRESS: 'Progress',
  ADMISSION: 'Admission',
  DISCHARGE_SUMMARY: 'Discharge Summary',
};

const NOTE_TYPE_COLORS: Record<ClinicalNoteType, 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'default'> = {
  VET_ROUND: 'primary',
  SHIFT_HANDOFF: 'secondary',
  PROGRESS: 'default',
  ADMISSION: 'success',
  DISCHARGE_SUMMARY: 'warning',
};

type TabValue = 'ALL' | 'VET_ROUND' | 'SHIFT_HANDOFF';

// ─── Note Card ────────────────────────────────────────────────────────────────

interface NoteCardProps {
  note: ClinicalNote;
}

const NoteCard: React.FC<NoteCardProps> = ({ note }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        '&:last-child': { borderBottom: 'none' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
        <Chip
          label={NOTE_TYPE_LABELS[note.noteType]}
          size="small"
          color={NOTE_TYPE_COLORS[note.noteType]}
          variant="outlined"
        />
        <Typography variant="caption" color="text.secondary">
          {note.author?.name ?? 'Unknown'}
        </Typography>
        <Typography variant="caption" color="text.disabled" sx={{ ml: 'auto' }}>
          {format(parseISO(note.createdAt), 'h:mm a, MMM d yyyy')}
        </Typography>
      </Box>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
        {note.body}
      </Typography>
    </Box>
  );
};

// ─── Add Note Form ────────────────────────────────────────────────────────────

interface AddNoteFormProps {
  sheetId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

const AddNoteForm: React.FC<AddNoteFormProps> = ({ sheetId, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const [noteType, setNoteType] = useState<ClinicalNoteType>('PROGRESS');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation(
    () =>
      treatmentSheetApi.addNote(sheetId, { noteType, body } as AddNotePayload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['clinical-notes', sheetId]);
        setBody('');
        setNoteType('PROGRESS');
        onSuccess();
      },
      onError: () => setError('Failed to save note. Please try again.'),
    },
  );

  const handleSubmit = () => {
    if (!body.trim()) {
      setError('Note body cannot be empty.');
      return;
    }
    setError(null);
    mutation.mutate();
  };

  return (
    <Box
      sx={{
        p: 2,
        borderBottom: 1,
        borderColor: 'divider',
        backgroundColor: 'action.hover',
      }}
    >
      <Typography variant="subtitle2" fontWeight={600} gutterBottom>
        Add Clinical Note
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <FormControl size="small" sx={{ maxWidth: 260 }}>
          <InputLabel>Note Type</InputLabel>
          <Select
            label="Note Type"
            value={noteType}
            onChange={(e) => setNoteType(e.target.value as ClinicalNoteType)}
          >
            {(Object.keys(NOTE_TYPE_LABELS) as ClinicalNoteType[]).map((k) => (
              <MenuItem key={k} value={k}>
                {NOTE_TYPE_LABELS[k]}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          label="Note"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          multiline
          minRows={3}
          fullWidth
          size="small"
          placeholder="Enter clinical note..."
        />
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Button size="small" onClick={onCancel} disabled={mutation.isLoading}>
            Cancel
          </Button>
          <Button
            size="small"
            variant="contained"
            onClick={handleSubmit}
            disabled={mutation.isLoading || !body.trim()}
            startIcon={mutation.isLoading ? <CircularProgress size={14} /> : undefined}
          >
            Save Note
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface ClinicalNotesSectionProps {
  sheetId: string;
}

const ClinicalNotesSection: React.FC<ClinicalNotesSectionProps> = ({ sheetId }) => {
  const rbac = useRBAC();
  const can = treatmentSheetControls(rbac);
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState<TabValue>('ALL');
  const [showForm, setShowForm] = useState(false);

  const { data: notes = [], isLoading, isError } = useQuery<ClinicalNote[]>(
    ['clinical-notes', sheetId],
    () => treatmentSheetApi.listNotes(sheetId),
  );

  const filteredNotes =
    activeTab === 'ALL'
      ? notes
      : notes.filter((n) => n.noteType === activeTab);

  return (
    <Paper sx={{ overflow: 'hidden' }}>
      {/* Header row */}
      <Box
        sx={{
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NoteIcon fontSize="small" color="action" />
          <Typography variant="h6" fontWeight={600}>
            Clinical Notes
          </Typography>
        </Box>
        {/* clinical-note.controller.ts `create` :21-22 → manage:medical_records. The notes
            THEMSELVES stay visible: reading them is `view:medical_records`, which this screen's
            route already required. */}
        {can.notes && (
          <Button
            size="small"
            startIcon={<AddIcon />}
            variant={showForm ? 'outlined' : 'contained'}
            onClick={() => setShowForm((p) => !p)}
          >
            {showForm ? 'Cancel' : 'Add Note'}
          </Button>
        )}
      </Box>

      {/* Add note form */}
      <Collapse in={showForm}>
        <AddNoteForm
          sheetId={sheetId}
          onSuccess={() => setShowForm(false)}
          onCancel={() => setShowForm(false)}
        />
      </Collapse>

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v: TabValue) => setActiveTab(v)}
        sx={{ px: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label={`All (${notes.length})`} value="ALL" />
        <Tab
          label={`Vet Rounds (${notes.filter((n) => n.noteType === 'VET_ROUND').length})`}
          value="VET_ROUND"
        />
        <Tab
          label={`Shift Handoffs (${notes.filter((n) => n.noteType === 'SHIFT_HANDOFF').length})`}
          value="SHIFT_HANDOFF"
        />
      </Tabs>

      {/* Note list */}
      <Box>
        {isLoading && (
          <Box sx={{ py: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={28} />
          </Box>
        )}
        {isError && (
          <Box sx={{ p: 2 }}>
            <Alert severity="error">Failed to load clinical notes.</Alert>
          </Box>
        )}
        {!isLoading && !isError && filteredNotes.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.disabled">
              No{activeTab !== 'ALL' ? ` ${NOTE_TYPE_LABELS[activeTab as ClinicalNoteType]}` : ''}{' '}
              notes yet.
            </Typography>
          </Box>
        )}
        {!isLoading &&
          !isError &&
          filteredNotes.map((note) => <NoteCard key={note.id} note={note} />)}
      </Box>
    </Paper>
  );
};

export default ClinicalNotesSection;
